<script>
import dayjs from 'dayjs';
import {
  pageTitleMixin,
  prefetchDataMixin,
} from '@ubic/vue-mixins';
import { defaultWeekends } from '@/utils';
import { permissions } from '@/constants';
import ErrorAccessDenied from '@/components/ErrorAccessDenied';
import WeekendsControl from './components/WeekendsControl';
import service from './service';

import Calendar from './components/Calendar';
import AddYear from './components/AddYearModal';

const curYear = dayjs().year();

const yearsRange = years => years.map(year => ({
  text: Number(year).toString(),
  value: Number(year).toString(),
}));

export default {
  pageTitle: 'Производственный календарь',
  prefetchData () {
    this.fetch();
  },
  mixins: [
    pageTitleMixin,
    prefetchDataMixin,
  ],
  data () {
    return {
      isFetching: false,
      isSaving: false,
      initial: [],
      dates: [],
      years: [],
      year: 2020,
      id: '',
      ranges: yearsRange([curYear]),

      showAdd: false,
    };
  },
  computed: {
    hasWritePermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.WEEKENDS_WRITE);
    },

    sameDates () {
      const { initial, dates } = this;

      if (initial.length > 0) {
        const temp = [];

        dates.forEach(el => {
          if (initial.includes(el)) {
            temp.push('');
          }
        });
        return temp.length === initial.length && temp.length === dates.length;
      }

      return false;
    },
  },
  watch: {
    // eslint-disable-next-line
    async '$route.query.year' (val) {
      if (!val || !Number(val)) {
        this.year = curYear;
      } else {
        this.year = Number(val);
      }

      if (!this.years.includes(this.year)) {
        this.years = [...this.years, this.year];
        this.ranges = yearsRange(this.years);
      }

      this.fetchDates();
    },
  },
  methods: {
    async fetch () {
      this.isFetching = true;

      const year = this.$route.query.year || curYear;
      this.year = Number(year) || curYear;

      try {
        const { years } = await service.getYears();
        this.years = years || [];

        if (this.years.includes(this.year)) {
          await this.fetchDates(true);
        } else {
          this.years.push(this.year);
          this.dates = await defaultWeekends(this.year);
        }
      } catch (err) {
        this.years = [this.year];
        this.dates = await defaultWeekends(this.year);
      }

      this.ranges = yearsRange(this.years);

      this.isFetching = false;
    },
    async fetchDates (first = false) {
      if (!first) {
        this.isFetching = true;
      }

      try {
        const data = await service.getByYear(this.year);
        const cleared = this.clearLoadedDates(data.dates);

        this.dates = cleared;
        this.initial = cleared;
        this.id = data.id;
      } catch (err) {
        this.dates = await defaultWeekends(this.year);
      }

      if (!first) {
        this.isFetching = false;
      }
    },

    handleCloseNoRights () {
      this.$emit('close');
      this.$router.replace('/');
    },

    handleDatesUpdate (dates) {
      if (!this.hasWritePermission) {
        return false;
      }
      this.dates = dates;
    },

    async handleCreateYearWeekends () {
      if (!this.hasWritePermission) {
        return false;
      }

      this.isSaving = true;
      const { dates, year } = this;

      try {
        const data = await service.create({ dates, year });
        const cleared = this.clearLoadedDates(data.dates);

        this.dates = cleared;
        this.initial = cleared;
        this.id = data.id;
      } catch (err) {
        this.isSaving = false;
      }

      this.isSaving = false;
    },

    async handleUpdateYearWeekends () {
      if (!this.hasWritePermission) {
        return false;
      }

      this.isSaving = true;
      const { dates, year, id } = this;

      try {
        const data = await service.update(year, { dates, year, id });
        const cleared = this.clearLoadedDates(data.dates);

        this.dates = cleared;
        this.initial = cleared;
      } catch (err) {
        this.isSaving = false;
      }

      this.isSaving = false;
    },

    clearLoadedDates (dates) {
      return dates.map(el => el.substr(0, 10));
    },

    handleChangeYear (value) {
      this.year = Number(value) || curYear;
      this.initial = [];
      this.dates = [];
      this.updateQueryString();
    },

    updateQueryString () {
      const { year } = this;
      this.$router.replace({
        ...this.$route,
        query: { year },
      });
    },

    handleAdd () {
      this.showAdd = true;
    },
    handleAddClose () {
      this.showAdd = false;
    },
    async handleAddYear (year) {
      if (!this.hasWritePermission) {
        return false;
      }

      this.years.push(year);
      this.ranges = yearsRange(this.years);
      this.showAdd = false;

      this.handleChangeYear(year);
    },
  },
  render () {
    if (!this.hasWritePermission) {
      return (
        <u-modal onClose={this.handleCloseNoRights}>
          <ErrorAccessDenied />
        </u-modal>
      );
    }

    return (
      <div>
        <u-title>
          Производственный календарь на&nbsp;
          {this.year}
          &nbsp;год
        </u-title>

        {this.isFetching ? (
          <u-spinner />
        ) : (
          <div class={{ disabled: this.isSaving }}>
            <WeekendsControl
              year={this.year}
              ranges={this.ranges}
              initial={this.initial}
              same={this.sameDates}
              fetching={this.isFetching}
              saving={this.isSaving}
              canEdit={this.hasWritePermission}
              onChangeyear={this.handleChangeYear}
              onSave={this.handleCreateYearWeekends}
              onUpdate={this.handleUpdateYearWeekends}
              onAdd={this.handleAdd}
            />

            <Calendar
              weekends={this.dates}
              year={this.year}
              canEdit={this.hasWritePermission}
              onDatesupdate={this.handleDatesUpdate}
            />

            {this.showAdd && (
              <AddYear
                years={this.years}
                onClose={this.handleAddClose}
                onAdd={this.handleAddYear}
              />
            )}
          </div>
        )}
      </div>
    );
  },
};
</script>
